import { useRef, useState, useCallback, useEffect, type KeyboardEvent, type ClipboardEvent } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface TwoFactorCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  error?: boolean;
}

export function TwoFactorCodeInput({
  value,
  onChange,
  disabled = false,
  autoFocus = false,
  error = false,
}: TwoFactorCodeInputProps): JSX.Element {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [internalValues, setInternalValues] = useState<string[]>(() =>
    value.split('').concat(Array.from({ length: 6 - value.length }, () => '')).slice(0, 6)
  );

  // Sync internal values with external value
  useEffect(() => {
    const newValues = value.split('').concat(Array.from({ length: 6 - value.length }, () => '')).slice(0, 6);
    setInternalValues(newValues);
  }, [value]);

  const focusInput = useCallback((index: number): void => {
    if (index >= 0 && index < 6) {
      inputRefs.current[index]?.focus();
    }
  }, []);

  const handleChange = useCallback((index: number, inputValue: string): void => {
    // Only accept digits
    const digit = inputValue.replace(/\D/g, '').slice(-1);

    const newValues = [...internalValues];
    newValues[index] = digit;
    setInternalValues(newValues);

    // Notify parent
    const newValue = newValues.join('');
    onChange(newValue);

    // Move to next input if a digit was entered
    if (digit && index < 5) {
      focusInput(index + 1);
    }
  }, [internalValues, onChange, focusInput]);

  const handleKeyDown = useCallback((index: number, e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Backspace') {
      if (!internalValues[index] && index > 0) {
        // Move to previous input on backspace if current is empty
        focusInput(index - 1);
      } else {
        // Clear current input
        const newValues = [...internalValues];
        newValues[index] = '';
        setInternalValues(newValues);
        onChange(newValues.join(''));
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusInput(index - 1);
    } else if (e.key === 'ArrowRight' && index < 5) {
      e.preventDefault();
      focusInput(index + 1);
    }
  }, [internalValues, onChange, focusInput]);

  const handlePaste = useCallback((e: ClipboardEvent<HTMLInputElement>): void => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const digits = pastedText.replace(/\D/g, '').slice(0, 6);

    if (digits) {
      const newValues = digits.split('').concat(Array.from({ length: 6 - digits.length }, () => '')).slice(0, 6);
      setInternalValues(newValues);
      onChange(newValues.join(''));

      // Focus the appropriate input after paste
      const focusIndex = Math.min(digits.length, 5);
      focusInput(focusIndex);
    }
  }, [onChange, focusInput]);

  // Auto focus first input
  useEffect(() => {
    if (autoFocus) {
      focusInput(0);
    }
  }, [autoFocus, focusInput]);

  return (
    <div className="flex gap-2 justify-center" data-testid="2fa-code-input">
      {Array.from({ length: 6 }, (_, index) => (
        <Input
          key={index}
          ref={(el) => { inputRefs.current[index] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={internalValues[index] ?? ''}
          onChange={(e) => { handleChange(index, e.target.value); }}
          onKeyDown={(e) => { handleKeyDown(index, e); }}
          onPaste={handlePaste}
          disabled={disabled}
          className={cn(
            'w-12 h-14 text-center text-2xl font-semibold',
            error && 'border-destructive focus-visible:ring-destructive'
          )}
          data-testid={`2fa-code-digit-${String(index)}`}
        />
      ))}
    </div>
  );
}
